export const GRID_SIZE = 4;

/** Create an empty 4x4 grid */
export function createEmptyGrid() {
    return Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
}

/** Place a random tile (90% chance of 2, 10% chance of 4) in a random empty cell */
export function addRandomTile(grid) {
    const empty = [];
    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (grid[r][c] === null) empty.push([r, c]);
        }
    }
    if (empty.length === 0) return grid;
    const [r, c] = empty[Math.floor(Math.random() * empty.length)];
    const next = grid.map((row) => [...row]);
    next[r][c] = Math.random() < 0.9 ? 2 : 4;
    return next;
}

/** Initialize a new game grid with 2 random tiles */
export function initGrid() {
    return addRandomTile(addRandomTile(createEmptyGrid()));
}

/**
 * Slide and merge a single row to the left.
 * Returns { row, score } where score is the points earned from merges.
 */
function slideRow(row) {
    // Remove nulls
    const tiles = row.filter((v) => v !== null);
    const result = [];
    let score = 0;

    for (let i = 0; i < tiles.length; i++) {
        if (i + 1 < tiles.length && tiles[i] === tiles[i + 1]) {
            const merged = tiles[i] * 2;
            result.push(merged);
            score += merged;
            i++; // skip next
        } else {
            result.push(tiles[i]);
        }
    }

    // Pad with nulls
    while (result.length < GRID_SIZE) result.push(null);
    return { row: result, score };
}

/** Transpose a grid (swap rows/cols) */
function transpose(grid) {
    return grid[0].map((_, c) => grid.map((row) => row[c]));
}

/** Reverse each row */
function reverseRows(grid) {
    return grid.map((row) => [...row].reverse());
}

/**
 * Move tiles in the given direction.
 * Returns { grid, score, moved } — moved is false if nothing changed.
 */
export function move(grid, direction) {
    let working = grid.map((row) => [...row]);
    let totalScore = 0;

    // Normalize all directions to "slide left", then undo the transformation
    if (direction === 'up') working = transpose(working);
    else if (direction === 'down') working = reverseRows(transpose(working));
    else if (direction === 'right') working = reverseRows(working);

    const newRows = working.map((row) => {
        const { row: slid, score } = slideRow(row);
        totalScore += score;
        return slid;
    });

    // Undo transformation
    let result = newRows;
    if (direction === 'up') result = transpose(result);
    else if (direction === 'down') result = transpose(reverseRows(result));
    else if (direction === 'right') result = reverseRows(result);

    const moved = !gridsEqual(grid, result);
    return { grid: result, score: totalScore, moved };
}

function gridsEqual(a, b) {
    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (a[r][c] !== b[r][c]) return false;
        }
    }
    return true;
}

/** Check if any moves are still possible */
export function canMove(grid) {
    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (grid[r][c] === null) return true;
            if (c + 1 < GRID_SIZE && grid[r][c] === grid[r][c + 1]) return true;
            if (r + 1 < GRID_SIZE && grid[r][c] === grid[r + 1][c]) return true;
        }
    }
    return false;
}

/** Check if the grid contains a 2048 tile */
export function hasWon(grid) {
    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (grid[r][c] >= 2048) return true;
        }
    }
    return false;
}

/** Tile background color mapping */
export const TILE_COLORS = {
    2: '#eee4da',
    4: '#ede0c8',
    8: '#f2b179',
    16: '#f59563',
    32: '#f67c5f',
    64: '#f65e3b',
    128: '#edcf72',
    256: '#edcc61',
    512: '#edc850',
    1024: '#edc53f',
    2048: '#edc22e',
};

/** Text color — dark for low tiles, white for high tiles */
export function tileTextColor(value) {
    return value <= 4 ? '#776e65' : '#f9f6f2';
}
